import React from 'react';
import WineGlassIcon from './icons/WineGlassIcon';

interface LoadingSpinnerProps {
    title?: string;
    type?: 'analyzing' | 'loading';
    showAdvancedUI?: boolean;
    showTimer?: boolean;
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ 
    title, 
    type = 'analyzing',
    showAdvancedUI = false,
    showTimer = true
}) => {
    const analyzingMessages = [
        "最高のワインを選んでいます...",
        "ワインリストをテイスティング中...",
        "あなたにぴったりの一本を探しています...",
        "ソムリエが熟考中です...",
    ];

    const loadingMessages = [
        "データを読み込み中...",
        "アプリを初期化中...",
        "設定を確認中...",
        "準備中...",
    ];

    const messages = type === 'analyzing' ? analyzingMessages : loadingMessages;
    const defaultTitle = type === 'analyzing' ? '分析中' : '読み込み中';

    const [message, setMessage] = React.useState(messages[0]);
    const [elapsedTime, setElapsedTime] = React.useState(0);

    React.useEffect(() => {
        const interval = setInterval(() => {
            setMessage(prevMessage => {
                const currentIndex = messages.indexOf(prevMessage);
                const nextIndex = (currentIndex + 1) % messages.length;
                return messages[nextIndex];
            });
        }, 3000);

        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (!showTimer) return;

        const startTime = Date.now();
        const timer = setInterval(() => {
            const elapsed = Math.floor((Date.now() - startTime) / 1000);
            setElapsedTime(elapsed);
        }, 1000);

        return () => clearInterval(timer);
    }, [showTimer]);

    if (showAdvancedUI && type === 'analyzing') {
        return (
            <div className="min-h-screen theme-bg-primary flex items-center justify-center" style={{background: 'linear-gradient(135deg, var(--theme-bg-primary), var(--theme-bg-secondary))'}}>
                <div className="text-center max-w-lg mx-auto p-8">
                    <div className="mb-10">
                        <div className="relative mb-6 flex justify-center">
                            <div
                              className="animate-wine-sway"
                              style={{ transformOrigin: '50% 92%' }}
                            >
                              <WineGlassIcon className="w-28 h-40 mx-auto text-red-800 animate-wine-shimmer" />
                            </div>
                        </div>
                        <h2 className="text-3xl font-bold theme-text-primary mb-3">AIソムリエが分析中...</h2>
                        <p className="text-lg theme-text-secondary">あなたの回答を分析して、最適なワインを選んでいます</p>
                    </div>
                    
                    <div className="mb-10">
                        <div className="theme-bg-secondary/60 backdrop-blur-sm rounded-2xl p-6 shadow-lg theme-border" style={{backgroundColor: 'rgba(var(--theme-bg-secondary-rgb, 255, 255, 255), 0.6)', borderColor: 'var(--theme-border)'}}>
                            <div className="space-y-4">
                                <div className="flex items-center justify-center gap-3">
                                    <div className="w-3 h-3 bg-red-400 rounded-full animate-pulse"></div>
                                    <span className="theme-text-primary font-medium">あなたの回答を分析中...</span>
                                </div>
                                <div className="flex items-center justify-center gap-3">
                                    <div className="w-3 h-3 bg-red-400 rounded-full animate-pulse" style={{animationDelay: '0.3s'}}></div>
                                    <span className="theme-text-primary font-medium">ワインリストと照合中...</span>
                                </div>
                                <div className="flex items-center justify-center gap-3">
                                    <div className="w-3 h-3 bg-red-400 rounded-full animate-pulse" style={{animationDelay: '0.6s'}}></div>
                                    <span className="theme-text-primary font-medium">最適なワインを選択中...</span>
                                </div>
                            </div>
                            
                            {showTimer && (
                                <div className="mt-6">
                                    <div className="theme-bg-secondary/40 backdrop-blur-sm rounded-xl p-4 theme-border" style={{backgroundColor: 'rgba(var(--theme-bg-secondary-rgb, 255, 255, 255), 0.4)', borderColor: 'var(--theme-border)'}}>
                                        <div className="flex items-center justify-center gap-2">
                                            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                                            <span className="theme-text-primary font-medium">
                                                分析開始から {elapsedTime} 秒経過
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    <div className="text-center">
                        <p className="theme-text-secondary text-sm">しばらくお待ちください...</p>
                        <p className="theme-text-secondary text-xs mt-2 transition-opacity duration-500">{message}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center justify-center text-center">
            <div className="relative w-24 h-24">
                <div className="absolute border-4 border-red-200 rounded-full w-full h-full"></div>
                <div className="absolute border-4 border-t-red-800 rounded-full w-full h-full animate-spin"></div>
            </div>
            <h2 className="text-2xl font-semibold text-gray-700 mt-6">{title || defaultTitle}</h2>
            <p className="text-gray-600 mt-2 transition-opacity duration-500">{message}</p>
        </div>
    );
};

export default LoadingSpinner;
